import {
    type ChangeEvent,
    type FC,
    useEffect,
    useRef,
    useState
} from 'react'

import './_writeMessage.scss'
import EmojiPicker, {
  Categories,
  SuggestionMode
} from 'emoji-picker-react'
import { useTranslation } from 'react-i18next'
import { FiPlusCircle } from 'react-icons/fi'
import { useReactMediaRecorder } from 'react-media-recorder'
import { useLocation, useNavigate } from 'react-router-dom'

import { IconCalendarOutlineDot, IconClose, IconGif } from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
// import AddPayoutMethodModal from 'components/UI/Modal/AddPayoutMethod/AddPayoutMethod' // dfsd
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import DateModal from 'components/UI/Modal/Date/DateModal'
import SubscribeProfileModal from 'components/UI/Modal/profileModals/SubscribeProfileModal/SubscribeProfileModal'
import SharedMediaModal from 'components/UI/Modal/SharedMediaModal/SharedMediaModal'
import SharedMediaModalUnshared from 'components/UI/Modal/SharedMediaModal/SharedMediaModalUnshared'
import { AudioInMessage, ScheduledInfoBubble } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { AudioRecorder } from 'features/MediaRecorder/AudioRecorder'
import PostMediaRecorder from 'features/MediaRecorder/MediaRecorder'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { AllIcons } from 'helpers/allIcons'
import { useSubscribeMutation } from 'helpers/hooks/subscription/useSubscribeMutation'
import { useSubscriptionPlans } from 'helpers/hooks/subscription/useSubscriptionPlans'
import { useAudioRecorder } from 'helpers/hooks/useAudioRecorder/useAudioRecorder'
import { Icons } from 'helpers/icons'
import { randomString } from 'helpers/util'
import { useUserProfile } from 'store'
import { useInboxCacheStore } from 'store/inboxCacheStore'
import { type IMessage } from 'types/interfaces/IMessage'
import { type IProgressInfo } from 'types/interfaces/ITypes'

const WriteMessage: FC<{
    message: IMessage
    setMessage: (message: any) => void
    uploadMedia: (data: File[]) => void
    sendMessage: () => void
    customClass?: string
    sending?: boolean
    selectFiles?: (
        event: ChangeEvent<HTMLInputElement>,
        groupId?: string | null,
        recipients?: string[] | null
        ) => any
    selectedFiles: File[]
    setSelectedFiles: any
    setProgressInfos: any
    sendingDisabled?: boolean
    progressInfos: { val: IProgressInfo[] }
    setGifOpen: () => void
    hasGif: boolean
    containerRef?: React.RefObject<HTMLDivElement>;
    removeFile: any
    setSchedule: (date: Date | string) => void
    schedule: string
    setReply?: (
        val: {
            body: string
            id: string | number
            message: any
            recieved: boolean
        } | null
    ) => void
    onFocusIn?: (val: string) => void
    submitFn?: (_val: any) => void
    onFocusOut?: (val: string) => void
    disabled?: boolean
    welcomeMessage?: boolean
    editWelcomeMessage?: boolean
    setMessageId?: (id: string | null) => void
    otherUserData?: any 
    conversationId?: string |null
    messagesReady?: boolean 
    appendAttachments?: (items: any[]) => void
    fanActionsOpen?: boolean
    setFanActionsOpen?: (open: boolean) => void
    isChatCooldown?: boolean
}> = ({
    message,
    setMessage,
    uploadMedia,
    sendMessage,
    customClass,
    sending,
    selectFiles,
    selectedFiles,
    setSelectedFiles,
    setProgressInfos,
    progressInfos,
    setGifOpen,
    hasGif,
    removeFile,
    setSchedule,
    schedule,
    onFocusIn,
    onFocusOut,
    // setReply,
    disabled,
    welcomeMessage = false,
    editWelcomeMessage = false,
    setMessageId,
    containerRef,
    otherUserData,
    conversationId,
    sendingDisabled = false,
    messagesReady,
    appendAttachments,
    fanActionsOpen = false,
    setFanActionsOpen,
    isChatCooldown = false,
}) => {
    const { t } = useTranslation()
    const modalData = useModalContext()
    const [activeChatIcon , setActiveChatIcon] = useState<boolean>(false)
    const [toggleEmojiPicker, setToggleEmojiPicker] = useState<boolean>(false)
    const [recording, setRecording] = useState<boolean>(false)
    const [tagsActive, setTagsActive] = useState<boolean>(false)
    const [recorderType, setRecorderType] = useState<string | null>(null)
    const [recordedChunks, setRecordedChunks] = useState([])
    const userData = useUserProfile()
    const location = useLocation();
    const [price, setPrice] = useState<number | null>(null)
    const formBtnRef = useRef<HTMLButtonElement>(null)
    const [chatUsers, setChatUsers] = useState<{
        all: any[]
        filter: string
        filteredUsers: any[]
    }>({
        all: [],
        filter: '',
        filteredUsers: []
    })
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)

    const inboxConversation = useInboxCacheStore(
    s => (otherUserData?.id ? s.getConversationByOtherUserId(otherUserData.id) : undefined)
    )
    const PlusCircleIcon = FiPlusCircle as React.FC<{
        size?: number | string
        color?: string
        className?: string
    }>

    const isFan = userData?.role === 'fan'
    const areBottomButtonsOpen = fanActionsOpen

    const hasActiveSubscription =
    Boolean(inboxConversation?.active_subscription) ||
    Boolean(otherUserData?.isSubscribed)


    const isChatConversationView = location.pathname.startsWith('/chat/')
    const showUnsubscribedPlaceholder =
        isChatConversationView &&
        userData?.role === 'model' &&
        Boolean(otherUserData?.id) &&
        !hasActiveSubscription 

    const shouldLoadSubscriptionPlans =
        Boolean(otherUserData?.id) &&
        !hasActiveSubscription &&
        Boolean(messagesReady)  &&
        userData?.role === 'fan'

    const {
        startRecording: startAudioRecording,
        stopRecording: stopAudioRecording,
        audioFile,
        recording: audioRecording
    } = useAudioRecorder()


    const { selectedSubscriptionPlan, handleSetSelectedSubscriptionPlan, queryKey } =
    useSubscriptionPlans({
        otherUserId: otherUserData?.id,
        shouldLoadSubscriptionPlans,
    })

    const { subscribeMutation } = useSubscribeMutation({
    otherUserId: otherUserData?.id,
    selectedSubscriptionPlan,
    t,
    onClose: () => setSubscribeModalOpen(false),
    invalidateKey: queryKey,
    })
        
    const isDisabled =
        (disabled ?? false) ||
        sendingDisabled ||
        (sending ?? false) ||
        isChatCooldown ||
        (!message.text && selectedFiles.length === 0)

    const payoutDisabled =
        userData?.role === 'model' &&
        userData?.payout_method == null
        
    const payoutModalOpenedRef = useRef(false)
    const navigate = useNavigate()

    useEffect(() => {
        if (modalData.isOpen === null) {
            setActiveChatIcon(false)
        }   
    }, [modalData])
   useEffect(() => {
    if (!userData) return

    if (payoutDisabled && !payoutModalOpenedRef.current) {
        payoutModalOpenedRef.current = true

        modalData.addModal(
            t('payoutRequired'),
            <ConfirmModal
                confirmButtonWidth="14"
                body={t('payoutRequiredDescription')}
                confirmText={t('gotoSettings')}
                confirmFn={() => {
                    navigate('/settings/general/payout-settings')
                }}
            />
        )
    }
    }, [userData, payoutDisabled, modalData, t])

    useEffect(() => {
        if (location.search) {
            const dateString = location.search.split('?date=')[1]
            setSchedule(new Date(dateString))
        }
    }, [])

    const { startRecording, stopRecording, mediaBlobUrl, status } =
        useReactMediaRecorder({
            audio: true,
            video: recorderType === 'video'
        })
useEffect(() => {
    if (!message.text && textAreaRef.current) {
        textAreaRef.current.style.height = ''
    }
}, [message.text])
    const postMediaRef = useRef<HTMLInputElement>(null)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const emojiPickerRef = useRef<HTMLDivElement>(null)
    const emojiButtonRef = useRef<HTMLImageElement>(null)


    useEffect(() => {
        const buttons = document.querySelectorAll('.epr-emoji')

        if (toggleEmojiPicker) {
            buttons.forEach(button => {
                button.setAttribute('type', 'button')
            })
        }
    }, [toggleEmojiPicker])

useEffect(() => {
  const convertBlob = (blob: any) => {
    const file = new File([blob[0]], randomString(blob[0].type), {
      type: blob[0].type
    })

    setSelectedFiles([...selectedFiles, file])
    uploadMedia([file])
    setRecordedChunks([])
  }

  if (recordedChunks.length > 0) convertBlob(recordedChunks)
}, [recordedChunks])

    useEffect(() => {
        if (audioFile) {
            setSelectedFiles([...selectedFiles, audioFile])
            uploadMedia([audioFile])
            setProgressInfos({ val: [...progressInfos.val] })
            setRecordedChunks([])
        }

        // eslint-disable-next-line
    }, [audioFile])

    useEffect(() => {
        if (tagsActive) {
            setChatUsers({
                ...chatUsers,
                filteredUsers: chatUsers.all.filter(
                    user =>
                        user.name.includes(chatUsers.filter) ||
                        user.username.includes(chatUsers.filter)
                )
            })
        }
    }, [chatUsers.filter])

    const onEmojiClick = (emojiObject: { emoji: string }) => {
        const newText = `${message.text}${emojiObject.emoji}`

        setMessage({ ...message, text: newText })
        setToggleEmojiPicker(false)
    }
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(event.target as Node) &&
                emojiButtonRef.current &&
                !emojiButtonRef.current.contains(event.target as Node)
            ) {
                setToggleEmojiPicker(false)
            }
        }

        if (toggleEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [toggleEmojiPicker])


useEffect(() => {
  const convertBlob = async (blob: any) => {
    const file = await fetch(blob)
      .then(async r => await r.blob())
      .then(blobFile => {
        return new File([blobFile], randomString(blobFile.type), {
          type: blobFile.type
        })
      })

    setSelectedFiles([...selectedFiles, file])
    uploadMedia([file])
    setRecordedChunks([])
  }

  if (mediaBlobUrl) convertBlob(mediaBlobUrl)
}, [mediaBlobUrl])

    useEffect(() => {
        if (
            progressInfos.val.filter(
                (item: IProgressInfo) =>
                    item.type === 'sound' || item.type === 'audio'
            ).length > 0
        ) {
            setMessage((prevState: any) => {
                const object = { ...prevState, text: '' }
                return object
            })
        }
    }, [progressInfos])


    useEffect(() => {
        if (modalData.isOpen=== null) {
            setActiveChatIcon(false)
        }
    }, [modalData])

    return (
        <>
            <form
                className={`writeMessage ${customClass ?? ''} ${
                    message.vaultImages?.length === 0
                        ? ''
                        : 'writeMessage--small'
                }
            ${recorderType ? 'writeMessage--recording' : ''}
            ${disabled ? 'writeMessage--disabled' : ''}
        `}
            >
                <div className='writeMessage__top' ref={containerRef}>
                    <div className='writeMessage__field'>
                        {progressInfos.val.filter(
                            (item: IProgressInfo) =>
                                item.type === 'sound' || item.type === 'audio'
                        ).length > 0 && (
                            <div className='write-message-audio-preview'>
                                <div
                                    className='write-message-audio-preview-remove'
                                    onClick={() =>
                                        removeFile(
                                            selectedFiles.indexOf(
                                                selectedFiles.filter(
                                                    (item: File) =>
                                                        item.type.includes(
                                                            'audio'
                                                        )
                                                )[0]
                                            )
                                        )
                                    }
                                >
                                    <IconClose color='#778797' />
                                </div>
                                <div className='write-message-audio-preview-wrapper'>
                                    <AudioInMessage
                                        mediaUrl={
                                            progressInfos.val.filter(
                                                (item: IProgressInfo) =>
                                                    item.type === 'sound' ||
                                                    item.type === 'audio'
                                            )[0].previewUrl
                                        }
                                        processing={
                                            progressInfos.val.filter(
                                                (item: IProgressInfo) =>
                                                    item.type === 'sound' ||
                                                    item.type === 'audio'
                                            )[0].percentage > 0 &&
                                            progressInfos.val.filter(
                                                (item: IProgressInfo) =>
                                                    item.type === 'sound' ||
                                                    item.type === 'audio'
                                            )[0].percentage < 100
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        <>
                        { (otherUserData?.role === 'model') &&
                            (otherUserData?.isRestricted === true || otherUserData?.isBlocked === true)
                            ? (
                            <>
                                <textarea
                                placeholder={
                                    showUnsubscribedPlaceholder
                                        ? 'Subscription Expired'
                                        : `${t('message')}...`
                                }
                                disabled
                                style={{
                                    cursor: 'not-allowed',
                                    backgroundColor: '#f5f5f5',
                                    color: '#a1a1a1',
                                    paddingLeft: '20px',
                                }}
                                />
                            </>
                            ) : (
                            userData?.role === 'model' ||
                            window.location.pathname.includes('messages/new') ||
                            hasActiveSubscription
                            ) ? (
                            <textarea
                              className={`writeMessage__textarea ${
                                userData?.role === 'model' && !hasActiveSubscription
                                    ? 'writeMessage__textarea--unsubscribed'
                                    : ''
                                }`}
                                ref={textAreaRef}
                                onKeyPress={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    if (formBtnRef.current) {
                                    formBtnRef.current.click()
                                    }
                                }
                                }}
                                disabled={
                                progressInfos.val.filter(
                                    (item: IProgressInfo) =>
                                    item.type === 'sound' || item.type === 'audio'
                                ).length > 0
                                }
                                onFocus={() =>
                                onFocusIn
                                    ? onFocusIn(message.text)
                                    : null
                                }
                                onBlur={() =>
                                onFocusOut
                                    ? onFocusOut(message.text)
                                    : null
                                }
                                onChange={e => {
                                setMessage({
                                    ...message,
                                    text: e.target.value
                                })
                                if (textAreaRef.current) {
                                    textAreaRef.current.style.height = '50px'
                                    const computed = window.getComputedStyle(textAreaRef.current)
                                    const lines = Math.floor(
                                    textAreaRef.current.scrollHeight /
                                        parseInt(
                                        computed.getPropertyValue('line-height'),
                                        10
                                        )
                                    )

                                    if (lines > 2) {
                                    const height = textAreaRef.current.scrollHeight + 19
                                    textAreaRef.current.style.height = `${Math.min(
                                        height,
                                        165
                                    )}px`
                                    }
                                }
                                }}
                                onKeyDown={e => {
                                if (!tagsActive) {
                                    if (e.keyCode === 50 && e.key === '@') {
                                    setTagsActive(true)
                                    }
                                } else {
                                    if (e.keyCode === 50 && e.key === '@') {
                                    setTagsActive(false)
                                    setChatUsers({
                                        ...chatUsers,
                                        filter: '',
                                        filteredUsers: []
                                    })
                                    } else if (e.keyCode === 32) {
                                    setTagsActive(false)
                                    setChatUsers({
                                        ...chatUsers,
                                        filter: '',
                                        filteredUsers: []
                                    })
                                    } else if (e.keyCode === 8) {
                                    if (chatUsers.filter.length === 0) {
                                        setTagsActive(false)
                                        setChatUsers({
                                        ...chatUsers,
                                        filter: '',
                                        filteredUsers: []
                                        })
                                    } else {
                                        setChatUsers({
                                        ...chatUsers,
                                        filter: chatUsers.filter.slice(0, -1),
                                        filteredUsers: []
                                        })
                                        setMessage({
                                        ...message,
                                        text: message.text.slice(0, -1)
                                        })
                                    }
                                    } else {
                                    if (
                                        (e.keyCode >= 48 && e.keyCode <= 57) ||
                                        (e.keyCode >= 65 && e.keyCode <= 90)
                                    ) {
                                        setChatUsers({
                                        ...chatUsers,
                                        filter: `${chatUsers.filter}${e.key}`
                                        })
                                    }
                                    }
                                }
                                }}
                                value={message.text}
                                placeholder={
                                    isChatCooldown
                                        ? 'Chat On Cooldown..'
                                        : showUnsubscribedPlaceholder
                                            ? 'Subscription Expired'
                                            : `${t('message')}...`
                                }
                            />
                            ) : (
                            <>
                                <textarea
                                placeholder={`${t(' to resume chatting') }`}
                                disabled
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f5f5f5',
                                    color: '#ffffff',
                                    position: 'relative',
                                    paddingLeft: '84px',
                                    fontSize: '13px',
                                    WebkitTextFillColor: '#bebebe',
                                    fontWeight: '500'
                                }}
                                />
                                <span
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f5f5f5',
                                    color: '#007aff',
                                    position: 'absolute',
                                    top: 15,
                                    left: 16,
                                    fontSize: '13px',
                                    fontWeight: '500'
                                }}
                                onClick={e => {
                                    e.stopPropagation()
                                    setSubscribeModalOpen(true)
                                }}
                                >
                                {t('Subscribe')}
                                </span>
                            </>
                            )
                        }
            <div className='writeMessage__fieldActions'>
                <img
                    ref={emojiButtonRef}
                    src={Icons.smile}
                    alt={t('smile')}
                    className='writeMessage__field--smile'
                    onClick={() => {
                        if (userData?.status === 'restricted') {
                            addToast('error', t('messageReactRestricted'))
                            return
                        }

                        setToggleEmojiPicker(!toggleEmojiPicker)
                    }}
                />

                    {!welcomeMessage && !editWelcomeMessage && (
                        <div
                            className={`writeMessage__field--plus ${
                                areBottomButtonsOpen
                                    ? 'writeMessage__field--plus--active'
                                    : ''
                            }`}
                            onClick={() => {
                                if (userData?.status === 'restricted') {
                                    addToast('error', t('messageReactRestricted'))
                                    return
                                }

                                setFanActionsOpen?.(!areBottomButtonsOpen)
                            }}
                        >
                            <PlusCircleIcon />
                        </div>
                    )}
                        </div>
                        {toggleEmojiPicker && (
                            <div ref={emojiPickerRef} className='emojiPicker'>
                            <EmojiPicker
                                onEmojiClick={(emojiData) => {
                                onEmojiClick({ emoji: emojiData.emoji })
                                }}
                                previewConfig={{
                                showPreview: false
                                }}
                                autoFocusSearch={false}
                                suggestedEmojisMode={SuggestionMode.RECENT}
                                lazyLoadEmojis
                                height={270} 
                                width={200} 
                                skinTonesDisabled
                                categories={[
                                {
                                    category: Categories.SUGGESTED,
                                    name: 'Recent'
                                },
                                {
                                    category: Categories.SMILEYS_PEOPLE,
                                    name: 'Smileys & People'
                                }
                                ]}
                            />
                            </div>
                        )}
                        </>

                    </div>
                    <button
                        type='button'
                        onClick={sendMessage}
                        ref={formBtnRef}
                        disabled={isDisabled}
                        className={`writeMessage__sendBtn ${
                            welcomeMessage || editWelcomeMessage
                                ? 'writeMessage__sendBtn--welcome-message'
                                : ''
                        } ${
                            sending ? 'writeMessage__sendBtn--sending' : ''
                        } ${
                            isDisabled ? 'writeMessage__sendBtn--disabled' : ''
                        }`}
                    >
                        {welcomeMessage || editWelcomeMessage ? (
                            <span className='writeMessage__sendBtn__text'>
                                Save
                            </span>
                        ) : (
                            <img src={Icons.lightSend} alt={t('send')} />
                        )}
                    </button>
                </div>
                {message.vaultImages?.length === 0 ? (
                    <div
                        className={`writeMessage__bot ${
                            !areBottomButtonsOpen
                                ? 'writeMessage__bot--hidden'
                                : ''
                        } writeMessage__bot--fan-controlled ${
                            welcomeMessage || editWelcomeMessage
                                ? 'writeMessage__bot--welcome-message'
                                : ''
                        }`}
                    >
                        <IconButton
                            disabled={payoutDisabled}
                            icon={Icons.chat_img}
                            clickFn={() => {
                                if (userData?.status === 'restricted') {
                                    addToast(
                                        'error',
                                        t('messageReactRestricted')
                                    )
                                    return
                                }
                                return postMediaRef.current?.click()
                            }}
                            desc={t('addMedia')}
                            customClass='writeMessage__bot__btn'
                            input={
                                <input
                                    ref={postMediaRef}
                                    type='file'
                                    onChange={(e) => {
                                        const recipients = otherUserData?.id ? [otherUserData.id] : null
                                        selectFiles?.(e, null, recipients)

                                        if (!welcomeMessage && !editWelcomeMessage) {
                                            setFanActionsOpen?.(false)
                                        }
                                    }}
                                    multiple={true}
                                    id='message__file__upload'
                                    accept='image/*,video/*'
                                    hidden={true}
                                    // key={Math.random().toString(36)}
                                />
                            }
                        />
                        {location.pathname.startsWith('/messages/') && userData?.role === 'model' && (
                            <IconButton
                                disabled={payoutDisabled}
                                icon={activeChatIcon ? Icons.chat_imgb_active : Icons.chat_imgb}
                                clickFn={() => {
                                    setActiveChatIcon(true)
                                    if (userData?.status === 'restricted') {
                                        addToast(
                                            'error',
                                            t('messageReactRestricted')
                                        )
                                        return
                                    }
                                    modalData.addModal(
                                        '',
                                        <SharedMediaModal
                                            defaultType="messages"
                                            submitFn={(val: any[]) => {
                                            const media: any[] = [];

                                            val.forEach(item => {
                                                // --- Message Photos ---
                                                if (item?.message?.message?.photos) {
                                                item.message.message.photos.forEach((photo: any) =>
                                                    media.push({
                                                    ...photo,
                                                    percentage: 100,
                                                    previewUrl: photo.url,
                                                    storageUrl: photo.url,
                                                    fileName: `shared-photo-${photo.id}`,
                                                    type: 'photo',
                                                    })
                                                );
                                                }

                                                // --- Message Videos ---
                                                if (item?.message?.message?.videos) {
                                                    item.message.message.videos.forEach((video: any) => {
                                                        const vaultVideo = item.vaultable?.videos?.find(
                                                            (v: any) => v.id === video.id
                                                        )

                                                        media.push({
                                                            ...video,
                                                            percentage: 100,
                                                            previewUrl: video.url,
                                                            storageUrl: video.url,
                                                            fileName: `shared-video-${video.id}`,
                                                            type: 'video',

                                                            thumbnails:
                                                                vaultVideo?.thumbs?.map((thumb: any) => thumb.src) || [],

                                                            imagePreview: video.active_thumb ?? null,

                                                            preview: vaultVideo?.preview || false,
                                                        })
                                                    })
                                                }

                                                // --- Post Photos ---
                                                if (item?.post?.photos) {
                                                item.post.photos.forEach((photo: any) =>
                                                    media.push({
                                                    ...photo,
                                                    percentage: 100,
                                                    previewUrl: photo.url,
                                                    storageUrl: photo.url,
                                                    fileName: `shared-photo-${photo.id}`,
                                                    type: 'photo',
                                                    })
                                                );
                                                }

                                                // --- Post Videos ---
                                                if (item?.post?.videos) {
                                                item.post.videos.forEach((video: any) =>
                                                    media.push({
                                                    ...video,
                                                    percentage: 100,
                                                    previewUrl: video.url,
                                                    storageUrl: video.url,
                                                    fileName: `shared-video-${video.id}`,
                                                    type: 'video',
                                                    thumbnails: video.thumbs?.map((thumb: any) => thumb.src) || [],
                                                    preview: video.preview || false,
                                                    })
                                                );
                                                }

                                                // --- Story ---
                                                if (item?.story) {
                                                // Stories could be images or videos. Customize as needed:
                                                if (item.story.video_src) {
                                                    media.push({
                                                    ...item.story,
                                                    percentage: 100,
                                                    previewUrl: item.story.video_src,
                                                    storageUrl: item.story.video_src,
                                                    fileName: `shared-story-video-${item.story.id}`,
                                                    type: 'story_video',
                                                    });
                                                }
                                                if (item.story.image_src) {
                                                    media.push({
                                                    ...item.story,
                                                    percentage: 100,
                                                    previewUrl: item.story.image_src,
                                                    storageUrl: item.story.image_src,
                                                    fileName: `shared-story-image-${item.story.id}`,
                                                    type: 'story_image',
                                                    });
                                                }
                                                }

                                                // --- Direct Media (if your modal ever returns raw media objects) ---
                                                if (item?.url && item?.type) {
                                                // Not wrapped in post/message/story, already a photo/video/media
                                                media.push({
                                                    ...item,
                                                    percentage: 100,
                                                    previewUrl: item.url,
                                                    storageUrl: item.url,
                                                    fileName: `shared-media-${item.id}`,
                                                    type: item.type,
                                                });
                                                }
                                            });

                                            // Set media
                                            const mergedMedia = [...media];
                                            appendAttachments?.(mergedMedia);
                                            setFanActionsOpen?.(false)

                                            // Set the message price based on selected media (from any source)
                                            let price = 0;
                                            for (const item of val) {
                                                if (item?.message?.message?.price != null) {
                                                price = item.message.message.price;
                                                break;
                                                }
                                                if (item?.post?.price != null) {
                                                price = item.post.price;
                                                break;
                                                }
                                                if (item?.story?.price != null) {
                                                price = item.story.price;
                                                break;
                                                }
                                                if (item?.price != null) {
                                                price = item.price;
                                                break;
                                                }
                                            }

                                            setMessage((prev: any) => ({
                                                ...prev,
                                                price
                                            }));
                                            }}

                                            userMedia={otherUserData}
                                            setMessageId={setMessageId}
                                            checkingData={'checkingData'}
                                        />,
                                        true,
                                        true,
                                        'modal--unshared-zlow'
                                    )
                                }}
                                desc='Idk'
                                customClass='writeMessage__bot__btn'
                            />
                        )}
                        {location.pathname.startsWith('/chat/') && userData?.role === 'model' && (
                            <IconButton
                                disabled={payoutDisabled}
                                icon={activeChatIcon ? Icons.chat_imgb_active : Icons.chat_imgb}
                                clickFn={() => {
                                    setActiveChatIcon(true)
                                    if (userData?.status === 'restricted') {
                                        addToast(
                                            'error',
                                            t('messageReactRestricted')
                                        )
                                        return
                                    }
                                    modalData.addModal(
                                        '',
                                        <SharedMediaModalUnshared
                                        submitFn={(val: any[]) => {
                                            const media: any[] = []
                                          
                                            val.forEach(item => {
                                                if (item?.message?.message?.photos) {
                                                item.message.message.photos.forEach((photo: any) =>
                                                    media.push({
                                                    ...photo,
                                                    photo_id: photo.id,
                                                    percentage: 100,
                                                    previewUrl: photo.url,
                                                    storageUrl: photo.url,
                                                    fileName: `shared-photo-${photo.id}`,
                                                    type: 'photo'
                                                    })
                                                )
                                                }
                                                if (item?.message?.message?.videos) {
                                                item.message.message.videos.forEach((video: any) => {
                                                    // Prefer server-provided thumb if present
                                                    const activeThumb = video.active_thumb || null

                                                    // If you *also* have vaultVideo thumbs, keep them as a nicer gallery
                                                    const vaultVideo = item.vaultable?.videos?.find((v: any) => v.id === video.id)
                                                    const vaultThumbs = vaultVideo?.thumbs?.map((t: any) => t.src) || []

                                                    const thumbs = (vaultThumbs.length ? vaultThumbs : (activeThumb ? [activeThumb] : []))

                                                    media.push({
                                                    ...video,
                                                    video_id: video.id,
                                                    type: 'video',
                                                    fileName: `shared-video-${video.id}`,

                                                    // video playback url (m3u8)
                                                    previewUrl: video.url,
                                                    storageUrl: video.url,

                                                    percentage: 100,
                                                    processing: false,

                                                    thumbnails: thumbs,
                                                    imagePreview: activeThumb || thumbs[0] || null,
                                                    thumbsDone: true,
                                                    thumbTarget: thumbs.length || 1,
                                                    thumbs_from: 'server',

                                                    // optional but helps your prepareMessage() send an active_thumbnail index
                                                    activeThumbnailIndex: thumbs.length ? 0 : null,
                                                    thumbType: video.thumb_type ?? 'thumb',

                                                    preview: video.preview ?? vaultVideo?.preview ?? false,
                                                    })
                                                })
                                                }
                                            })
                                          
                                            // Set media
                                            const mergedMedia = [...media]
                                            appendAttachments?.(mergedMedia);
                                            setFanActionsOpen?.(false)
                                          
                                            // Set the message price based on selected media
                                            const mediaWithPrice = val.find(
                                              (item: any) => item?.message?.message?.price != null
                                            )
                                          
                                            const price = mediaWithPrice?.message?.message?.price ?? 0
                                          
                                            setMessage((prev: any) => ({
                                              ...prev,
                                              price
                                            }))
                                          }}
                                            userMedia={otherUserData}
                                            setMessageId={setMessageId}
                                            checkingData={'checkingData'}
                                            setPrice={setPrice}
                                            onClose={() => {
                                                setActiveChatIcon(false)
                                            }}
                                        />,
                                        true,
                                        true,
                                        'modal--unshared-zlow'
                                    )
                                }}
                                desc='Idk'
                                customClass='writeMessage__bot__btn'
                            />
                        )}
{/*                         <>
                            {!recording ? (
                                <IconButton
                                    disabled={payoutDisabled}
                                    icon={Icons.chat_mic}
                                    desc={t('recordAudioMessage')}
                                    customClass='writeMessage__bot__btn'
                                    clickFn={() => {
                                        if (userData?.status === 'restricted') {
                                            addToast(
                                                'error',
                                                t('messageReactRestricted')
                                            )
                                            return
                                        }
                                        setRecorderType('audio')
                                    }}
                                />
                            ) : (
                                <IconButton
                                    disabled={payoutDisabled}
                                    icon={AllIcons.video_pause}
                                    desc={t('stopRecording')}
                                    customClass='writeMessage__bot__btn writeMessage__bot__btn--audiopause'
                                    clickFn={() => {
                                        stopRecording()
                                        setRecording(false)
                                    }}
                                />
                            )}
                        </> */}
                        {!welcomeMessage && (
                            <IconButton
                                disabled={payoutDisabled}
                                icon={Icons.chat_vid}
                                clickFn={() => {
                                    if (userData?.status === 'restricted') {
                                        addToast(
                                            'error',
                                            t('messageReactRestricted')
                                        )
                                        return
                                    }
                                    setRecorderType('video')
                                }}
                                desc={t('addVideoFiles')}
                                customClass='writeMessage__bot__btn'
                            />
                        )}
                        {!welcomeMessage && hasGif && (
                            <div
                                className='writeMessage__bot__btn writeMessage__bot__gif'
                                onPointerDown={e => {
                                    e.stopPropagation()
                                }}
                                onClick={() => {
                                    if (userData?.status === 'restricted') {
                                        addToast(
                                            'error',
                                            t('messageReactRestricted')
                                        )
                                        return
                                    }

                                    setGifOpen()
                                }}
                            >
                                <IconGif />
                            </div>
                        )}
                        {!welcomeMessage && userData?.role === 'model' && (
                            <div
                                onClick={() => {
                                    // if (!schedule) setSchedule(new Date())
                                    modalData.addModal(
                                        t('scheduleMessage'),
                                        <DateModal
                                            confirmFn={(val: any) => {
                                                setSchedule(val)
                                            }}
                                            newDateVal={new Date(schedule || new Date())}
                                        />
                                    )
                                }}
                            >
                                <IconCalendarOutlineDot
                                    color={
                                        schedule === '' ? '#778797' : '#2894FF'
                                    }
                                />
                            </div>
                        )}
                        {schedule !== '' && (
                            <div className='write-message-schedule-message'>
                                <ScheduledInfoBubble
                                    customClass='write-message-schedule-message-bubble'
                                    removeSchedule={() => {
                                        setSchedule('')
                                    }}
                                    date={new Date(schedule)}
                                    noText={true}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    ''
                )}

                {recorderType === 'video' && (
                    <PostMediaRecorder
                        status={status}
                        type={recorderType}
                        handleClose={() => {
                            setRecorderType(null)
                        }}
                        setRecordedChunks={data => {
                            setRecordedChunks(data)
                        }}
                        handleAudioStart={() => {
                            startRecording()
                        }}
                        handleAudioStop={() => {
                            stopRecording()
                        }}
                    />
                )}

                {recorderType === 'audio' && (
                    <AudioRecorder
                        type={recorderType}
                        status={audioRecording}
                        handleClose={() => {
                            setRecorderType('')
                        }}
                        handleAudioStart={() => {
                            startAudioRecording()
                        }}
                        handleAudioStop={stopAudioRecording}
                    />
                )}
            </form>
            {subscribeModalOpen && (
                <ModalWrapper
                    open={subscribeModalOpen}
                    setOpen={setSubscribeModalOpen}
                    customClass='subscribe-profile-modal-modal'
                    hasCloseButton={true}
                >
                    <SubscribeProfileModal
                        displayName={otherUserData?.display_name}
                        avatar={otherUserData?.avatar?.url}
                        username={otherUserData?.username}
                        id={otherUserData?.id}
                        subscribeFn={(paymentMethod: {
                            method: 'card' | 'deposit'
                            cardId: string | undefined
                            rebill?: boolean // Made optional
                            taxRate?: any
                            taxRateFee?: any
                        }) => {
                            // if (status !== 'active') {
                            //     addToast('error', t('inactiveSubscribe'))
                            //     return
                            // }
                            subscribeMutation.mutate(paymentMethod)
                            // refetchUserProfile()
                        }}
                        isLoading={subscribeMutation.isLoading}
                        selected={selectedSubscriptionPlan}
                        setSelected={handleSetSelectedSubscriptionPlan}
                    />
                </ModalWrapper>
            )}
        </>
    )
}

export default WriteMessage
